'use client'

// Form-composer chrome — ported from the prototype postform.jsx:28-92
// (AiBadge, FSection, FLabel, MoneyInput, FStepper).
import * as React from 'react'
import { Check, Info, Minus, Plus, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { IconBtn } from '@/components/roostr'

export function AiBadge({ text = 'from your photos' }: { text?: string }) {
  return (
    <span className="inline-flex items-center gap-[3px] whitespace-nowrap rounded-full bg-primary-container px-2 py-[2px] text-[9.5px] font-bold tracking-[0.3px] text-on-primary-container">
      <Sparkles size={11} /> {text}
    </span>
  )
}

// Numbered form section with completion state (number circle → green check).
export interface FSectionProps {
  n: number
  title: React.ReactNode
  hint?: React.ReactNode
  done?: boolean
  children: React.ReactNode
}

export function FSection({ n, title, hint, done, children }: FSectionProps) {
  return (
    <section className="rounded-shape-lg border border-outline-variant bg-surface px-[18px] pb-[18px] pt-4 shadow-e1">
      <div className="mb-3 flex items-start gap-3">
        <span
          className={cn(
            'mt-px flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors duration-300',
            done ? 'bg-success' : 'bg-surface-high'
          )}
        >
          {done ? (
            <Check size={14} color="#fff" strokeWidth={3} />
          ) : (
            <span className="font-mono text-[11.5px] font-bold text-ink-muted">{n}</span>
          )}
        </span>
        <div>
          <div className="text-[15.5px] font-bold tracking-[-0.2px]">{title}</div>
          {hint ? <div className="mt-px text-[12px] text-ink-muted">{hint}</div> : null}
        </div>
      </div>
      <div className="pl-9">{children}</div>
    </section>
  )
}

export interface FLabelProps {
  children: React.ReactNode
  badge?: string | null
  warn?: string | null
}

export function FLabel({ children, badge, warn }: FLabelProps) {
  return (
    <div className="mb-[6px] flex items-center gap-2">
      <span className="text-[12px] font-bold text-ink-secondary">{children}</span>
      {badge ? <AiBadge text={badge} /> : null}
      {warn ? (
        <span className="inline-flex items-center gap-[3px] text-[9.5px] font-bold text-warning">
          <Info size={11} /> {warn}
        </span>
      ) : null}
    </div>
  )
}

export interface MoneyInputProps {
  value: number
  onChange: (v: number) => void
  width?: number
  'aria-label'?: string
}

export function MoneyInput({ value, onChange, width = 76, 'aria-label': ariaLabel }: MoneyInputProps) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-outline bg-surface-low px-[11px] py-[5px]">
      <input
        type="number"
        value={value}
        aria-label={ariaLabel}
        onChange={(e) => onChange(Math.max(0, +e.target.value || 0))}
        className="border-none bg-transparent text-right font-mono text-[13px] font-bold text-ink outline-none"
        style={{ width }}
      />
      <span className="text-[11px] text-ink-muted">Kč</span>
    </span>
  )
}

export interface FStepperProps {
  value: number
  set: (v: number) => void
  min: number
  max: number
  unit?: string
  step?: number
  /** Render the middle as an editable integer input (± still work). */
  editable?: boolean
  'aria-label'?: string
}

export function FStepper({
  value,
  set,
  min,
  max,
  unit = '',
  step = 1,
  editable = false,
  'aria-label': ariaLabel,
}: FStepperProps) {
  const clamp = (v: number) => Math.min(max, Math.max(min, v))
  const bump = (d: number) => set(clamp(value + d))
  // Local text state so the field can be emptied mid-typing; commits digits
  // only (integers), clamped to [min, max].
  const [text, setText] = React.useState<string | null>(null)
  const commit = (raw: string) => {
    const n = parseInt(raw.replace(/[^\d-]/g, ''), 10)
    set(clamp(Number.isFinite(n) ? n : min))
    setText(null)
  }
  return (
    <span className="inline-flex items-center gap-[2px] rounded-full bg-surface-low p-[2px]">
      <IconBtn size={27} onClick={() => bump(-step)} aria-label="Decrease">
        <Minus size={15} className="text-ink-secondary" />
      </IconBtn>
      {editable ? (
        <span className="inline-flex min-w-[52px] items-baseline justify-center gap-[2px]">
          <input
            inputMode="numeric"
            pattern="[0-9]*"
            value={text ?? String(value)}
            aria-label={ariaLabel}
            onFocus={(e) => e.target.select()}
            onChange={(e) => setText(e.target.value.replace(/[^\d]/g, ''))}
            onBlur={(e) => commit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit((e.target as HTMLInputElement).value)
            }}
            className="w-[44px] border-none bg-transparent text-center font-mono text-[13.5px] font-bold text-ink outline-none"
          />
          {unit ? <span className="font-mono text-[11px] font-bold text-ink-muted">{unit.trim()}</span> : null}
        </span>
      ) : (
        <span className="min-w-[52px] text-center font-mono text-[13.5px] font-bold text-ink">
          {value}
          {unit}
        </span>
      )}
      <IconBtn size={27} onClick={() => bump(step)} aria-label="Increase">
        <Plus size={15} className="text-ink-secondary" />
      </IconBtn>
    </span>
  )
}

// Shared text-input styling (prototype postform.jsx inputStyle).
export const postInputCls =
  'w-full rounded-shape-sm border border-outline bg-surface-low px-[13px] py-[10px] text-[13.5px] text-ink outline-none placeholder:text-ink-muted'
